// Canvas Paint Brush Effect - Artist Theme
class PaintBrush {
  constructor() {
    this.canvas = null;
    this.ctx = null;
    this.isPainting = false;
    this.lastPaintPoint = null;
    this.paintColor = null;
    this.minPaintDistance = 2;
    
    this.init();
  }

  init() {
    // Create canvas that scrolls with the page
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'paint-brush-canvas';
    this.canvas.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 9998;
      mix-blend-mode: difference;
      opacity: 0.9;
    `;
    
    // Insert canvas at body level to cover entire page including navbar
    document.body.insertBefore(this.canvas, document.body.firstChild);
    
    this.ctx = this.canvas.getContext('2d', { alpha: true });
    
    this.resize();
    this.bindEvents();
  }

  resize() {
    if (!this.ctx) return;
    
    // Get full document dimensions to cover entire page including navbar area
    const docWidth = Math.max(
      document.body.scrollWidth || 0,
      document.body.offsetWidth || 0,
      document.documentElement.clientWidth || 0,
      document.documentElement.scrollWidth || 0,
      document.documentElement.offsetWidth || 0,
      window.innerWidth || 0
    );
    const docHeight = Math.max(
      document.body.scrollHeight || 0,
      document.body.offsetHeight || 0,
      document.documentElement.clientHeight || 0,
      document.documentElement.scrollHeight || 0,
      document.documentElement.offsetHeight || 0,
      window.innerHeight || 0
    );
    
    const dpr = window.devicePixelRatio || 1;
    const currentWidth = this.canvas.width ? this.canvas.width / dpr : 0;
    const currentHeight = this.canvas.height ? this.canvas.height / dpr : 0;
    
    // Only resize if dimensions changed
    if (currentWidth !== docWidth || currentHeight !== docHeight) {
      // Save current canvas content if it exists and has content
      let imageData = null;
      if (currentWidth > 0 && currentHeight > 0 && this.canvas.width > 0 && this.canvas.height > 0) {
        try {
          imageData = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
        } catch (e) {
          // Canvas might be too large, skip saving
          imageData = null;
        }
      }
      
      // Set canvas size to full document
      this.canvas.width = docWidth * dpr;
      this.canvas.height = docHeight * dpr;
      this.canvas.style.width = docWidth + 'px';
      this.canvas.style.height = docHeight + 'px';
      
      // Reset transform and scale for high DPI
      this.ctx.setTransform(1, 0, 0, 1, 0, 0);
      this.ctx.scale(dpr, dpr);
      
      // Restore image data if we had content
      if (imageData && currentWidth > 0 && currentHeight > 0) {
        try {
          // Only restore what fits in the new canvas
          const restoreWidth = Math.min(currentWidth, docWidth);
          const restoreHeight = Math.min(currentHeight, docHeight);
          const cropImageData = this.ctx.createImageData(restoreWidth * dpr, restoreHeight * dpr);
          
          // Copy the relevant portion
          for (let y = 0; y < restoreHeight * dpr; y++) {
            for (let x = 0; x < restoreWidth * dpr; x++) {
              const srcIdx = (y * imageData.width + x) * 4;
              const dstIdx = (y * cropImageData.width + x) * 4;
              if (srcIdx < imageData.data.length && dstIdx < cropImageData.data.length) {
                cropImageData.data[dstIdx] = imageData.data[srcIdx];
                cropImageData.data[dstIdx + 1] = imageData.data[srcIdx + 1];
                cropImageData.data[dstIdx + 2] = imageData.data[srcIdx + 2];
                cropImageData.data[dstIdx + 3] = imageData.data[srcIdx + 3];
              }
            }
          }
          this.ctx.putImageData(cropImageData, 0, 0);
        } catch (e) {
          // If restoration fails, just continue without it
          console.warn('Could not restore canvas content:', e);
        }
      }
    }
  }

  getCanvasCoordinates(e) {
    // Canvas is positioned absolutely, need client coords + scroll
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    
    return {
      x: e.clientX + scrollX,
      y: e.clientY + scrollY
    };
  }

  addPaintStroke(x, y, color, size, isDrag = false) {
    if (!this.ctx) return;
    
    const minDist = this.minPaintDistance;
    
    // For drag strokes, check distance from last point
    if (isDrag && this.lastPaintPoint) {
      const dist = Math.sqrt(
        Math.pow(x - this.lastPaintPoint.x, 2) + 
        Math.pow(y - this.lastPaintPoint.y, 2)
      );
      if (dist < minDist) {
        return; // Too close, skip
      }
    }
    
    this.ctx.save();
    
    // Brush shape - elongated and textured
    let brushLength = size * (1.2 + Math.random() * 0.3);
    let brushWidth = size * (0.6 + Math.random() * 0.2);
    let angle = Math.random() * Math.PI * 2;
    
    // For drag strokes, align brush with movement direction
    if (isDrag && this.lastPaintPoint) {
      const dx = x - this.lastPaintPoint.x;
      const dy = y - this.lastPaintPoint.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      if (dist > 1) {
        // Align brush with drag direction
        angle = Math.atan2(dy, dx);
        // Make brush longer in direction of movement
        brushLength = size * (1.5 + Math.random() * 0.4);
      }
    }
    
    this.ctx.translate(x, y);
    this.ctx.rotate(angle);
    
    // Create brush mark with gradient
    const gradient = this.ctx.createLinearGradient(-brushLength/2, 0, brushLength/2, 0);
    const r = Math.floor(color[0] * 255);
    const g = Math.floor(color[1] * 255);
    const b = Math.floor(color[2] * 255);
    
    gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, 0.7)`);
    gradient.addColorStop(0.5, `rgba(${r}, ${g}, ${b}, 0.9)`);
    gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0.7)`);
    
    this.ctx.fillStyle = gradient;
    this.ctx.beginPath();
    this.ctx.ellipse(0, 0, brushLength/2, brushWidth/2, 0, 0, Math.PI * 2);
    this.ctx.fill();
    
    // Add brush texture - small random marks
    for (let i = 0; i < 3; i++) {
      const offsetX = (Math.random() - 0.5) * brushLength * 0.6;
      const offsetY = (Math.random() - 0.5) * brushWidth * 0.6;
      const textureSize = brushWidth * (0.2 + Math.random() * 0.3);
      
      this.ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.4)`;
      this.ctx.beginPath();
      this.ctx.ellipse(offsetX, offsetY, textureSize/2, textureSize/3, Math.random() * Math.PI, 0, Math.PI * 2);
      this.ctx.fill();
    }
    
    this.ctx.restore();
    
    // Add occasional paint splatter for texture
    if (Math.random() < 0.3) {
      const splatAngle = Math.random() * Math.PI * 2;
      const splatDist = size * (0.3 + Math.random() * 0.4);
      const splatX = x + Math.cos(splatAngle) * splatDist;
      const splatY = y + Math.sin(splatAngle) * splatDist;
      const splatSize = size * (0.15 + Math.random() * 0.2);
      
      this.ctx.fillStyle = `rgba(${r}, ${g}, ${b}, 0.5)`;
      this.ctx.beginPath();
      this.ctx.arc(splatX, splatY, splatSize, 0, Math.PI * 2);
      this.ctx.fill();
    }
    
    // Update last paint point
    this.lastPaintPoint = { x, y };
  }
  
  startPainting(e) {
    // Prevent text selection while painting
    e.preventDefault();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    document.body.style.mozUserSelect = 'none';
    document.body.style.msUserSelect = 'none';
    
    // Change cursor to crosshair for painting
    document.body.style.cursor = 'crosshair';
    
    this.isPainting = true;
    const coords = this.getCanvasCoordinates(e);
    
    // Choose a paint color for this stroke - lighter colors work better with difference blend
    const colors = [
      [0.7, 0.6, 0.65], // Light brown/tan
      [0.6, 0.7, 0.6], // Light green
      [0.6, 0.65, 0.7], // Light blue
      [0.7, 0.65, 0.6], // Light orange/peach
      [0.65, 0.6, 0.7], // Light purple
      [0.68, 0.62, 0.58], // Light umber
      [0.6, 0.62, 0.68], // Light blue-grey
      [0.75, 0.75, 0.75], // Light grey
    ];
    
    const baseColor = colors[Math.floor(Math.random() * colors.length)];
    const colorVariation = Math.random() * 0.08;
    this.paintColor = [
      Math.max(0, Math.min(1, baseColor[0] + (Math.random() - 0.5) * colorVariation)),
      Math.max(0, Math.min(1, baseColor[1] + (Math.random() - 0.5) * colorVariation)),
      Math.max(0, Math.min(1, baseColor[2] + (Math.random() - 0.5) * colorVariation)),
    ];
    
    this.lastPaintPoint = { x: coords.x, y: coords.y };
    
    // Add initial paint mark
    const size = 25 + Math.random() * 40;
    this.addPaintStroke(coords.x, coords.y, this.paintColor, size, true);
  }
  
  continuePainting(e) {
    if (!this.isPainting || !this.paintColor) return;
    
    const coords = this.getCanvasCoordinates(e);
    
    // Add paint mark along the path
    const size = 20 + Math.random() * 35;
    this.addPaintStroke(coords.x, coords.y, this.paintColor, size, true);
  }
  
  stopPainting() {
    // Re-enable text selection
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
    document.body.style.mozUserSelect = '';
    document.body.style.msUserSelect = '';
    
    // Restore normal cursor
    document.body.style.cursor = '';
    
    this.isPainting = false;
    this.lastPaintPoint = null;
    this.paintColor = null;
  }

  bindEvents() {
    // Mouse move - handle cursor and painting
    document.addEventListener('mousemove', (e) => {
      if (this.isPainting) {
        // If painting (dragging), add paint marks and prevent selection
        e.preventDefault();
        this.continuePainting(e);
      } else {
        // Set brush cursor on hover (when not over interactive elements)
        const target = e.target;
        const isInteractive = target.tagName === 'A' || 
                             target.tagName === 'BUTTON' || 
                             target.tagName === 'INPUT' || 
                             target.tagName === 'TEXTAREA' ||
                             target.tagName === 'SELECT' ||
                             target.closest('a, button, input, textarea, select');
        
        if (!isInteractive) {
          document.body.style.cursor = 'crosshair';
        } else {
          // Restore default cursor over interactive elements
          document.body.style.cursor = '';
        }
      }
    });
    
    // Mouse down - start painting
    document.addEventListener('mousedown', (e) => {
      // Only prevent default if we're actually going to paint
      // (not on buttons, links, inputs, etc.)
      const target = e.target;
      const isInteractive = target.tagName === 'A' || 
                           target.tagName === 'BUTTON' || 
                           target.tagName === 'INPUT' || 
                           target.tagName === 'TEXTAREA' ||
                           target.tagName === 'SELECT' ||
                           target.closest('a, button, input, textarea, select');
      
      if (!isInteractive) {
        this.startPainting(e);
      }
    });
    
    // Mouse up - stop painting
    document.addEventListener('mouseup', (e) => {
      this.stopPainting();
    });
    
    // Mouse leave window - stop painting
    document.addEventListener('mouseleave', (e) => {
      if (e.target === document) {
        this.stopPainting();
      }
    });
    
    // Window resize - update canvas size
    window.addEventListener('resize', () => {
      this.resize();
    });
    
    // Scroll - canvas scrolls naturally, but check if document grew
    let lastScrollHeight = document.documentElement.scrollHeight;
    window.addEventListener('scroll', () => {
      // Check if document height changed (dynamic content loaded)
      const currentScrollHeight = document.documentElement.scrollHeight;
      if (currentScrollHeight !== lastScrollHeight) {
        this.resize();
        lastScrollHeight = currentScrollHeight;
      }
    });
    
    // Also watch for content changes that might resize the document
    const resizeObserver = new ResizeObserver(() => {
      this.resize();
    });
    
    if (document.body) {
      resizeObserver.observe(document.body);
      resizeObserver.observe(document.documentElement);
    }
  }


  destroy() {
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }
  }
}

// Initialize when DOM is ready
function initPaintBrush() {
  try {
    window.paintBrush = new PaintBrush();
  } catch (error) {
    console.error('Failed to initialize paint brush:', error);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPaintBrush);
} else {
  setTimeout(initPaintBrush, 0);
}
